/**
 * Favorites Service
 *
 * Manages user's favorite words, phrases, and learning content.
 * Provides functionality to add, remove, and retrieve favorited items.
 */

import { SERVICE_NAMES } from "../core/constants";
import { InternalSDKOperations, LanguageCode, WordId } from "../types";
import { BaseService } from "./base-service";
import { NetworkDetectionManager } from "../utils/network-detection-manager";

/**
 * Service class for managing user favorites and saved content
 */
export class FavoritesService extends BaseService {
  protected serviceName = SERVICE_NAMES.FAVORITES;
  /**
   * Creates a new FavoritesService instance
   * @param internalOps - Internal SDK operations interface
   * @param networkManager - Optional shared network detection manager
   */
  constructor(
    internalOps: InternalSDKOperations,
    networkManager?: NetworkDetectionManager
  ) {
    super(internalOps, networkManager);
  }

  async getFavoriteWords(
    nativeLanguage: LanguageCode,
    targetLanguage: LanguageCode,
    version: string = "v1"
  ): Promise<{ favoriteWords: Record<string, boolean> }> {
    const response = await this.internalOps.makeRequest<{
      favoriteWords: Record<string, boolean>;
    }>(
      SERVICE_NAMES.FAVORITES,
      "getFavoriteWords",
      {
        nativeLanguage,
        targetLanguage,
      },
      version
    );

    // SDK now guarantees response is successful (throws error otherwise)
    return response.data;
  }

  async addToFavoriteWords(
    nativeLanguage: LanguageCode,
    targetLanguage: LanguageCode,
    wordId: WordId,
    version: string = "v1"
  ): Promise<void> {
    const functionName = "addToFavoriteWords";
    const parameters = {
      nativeLanguage,
      targetLanguage,
      wordId,
    };

    await this.executeWithOfflineSupport(functionName, parameters, () =>
      this.internalOps.makeRequest<{}>(
        SERVICE_NAMES.FAVORITES,
        functionName,
        parameters,
        version
      )
    );
  }

  async removeFromFavoriteWords(
    nativeLanguage: LanguageCode,
    targetLanguage: LanguageCode,
    wordId: WordId,
    version: string = "v1"
  ): Promise<void> {
    const functionName = "removeFromFavoriteWords";
    const parameters = {
      nativeLanguage,
      targetLanguage,
      wordId,
    };

    await this.executeWithOfflineSupport(functionName, parameters, () =>
      this.internalOps.makeRequest<{}>(
        SERVICE_NAMES.FAVORITES,
        functionName,
        parameters,
        version
      )
    );
  }
}

